import type { ReactNode } from 'react'

export type StatTone = 'blue' | 'green' | 'orange' | 'violet'

const tones: Record<StatTone, { tile: string; badge: string; line: string }> = {
  blue: { tile: 'border-dpl-primary/15 bg-dpl-primary/[0.05]', badge: 'bg-dpl-primary/10 text-dpl-primary', line: 'text-dpl-primary' },
  green: { tile: 'border-dpl-success/15 bg-dpl-success/[0.05]', badge: 'bg-dpl-success/10 text-dpl-success', line: 'text-dpl-success' },
  orange: { tile: 'border-dpl-accent/20 bg-dpl-accent/[0.07]', badge: 'bg-dpl-accent/15 text-[#C2410C]', line: 'text-dpl-accent' },
  violet: { tile: 'border-violet-200 bg-violet-50/70', badge: 'bg-violet-100 text-violet-700', line: 'text-violet-600' },
}

/**
 * A headline figure: what it is, the number, how it moved since the last month-end,
 * and its recent path. Whether up is good is the caller's to say — more assets are, more
 * debt is not.
 */
export function StatTile({
  icon,
  label,
  value,
  change,
  goodWhen = 'up',
  caption,
  trend,
  tone = 'blue',
  changeLabel = 'from last month-end',
}: {
  icon: ReactNode
  label: string
  value: string
  /** Percent against the last month-end; null where there is nothing to compare with. */
  change?: number | null
  goodWhen?: 'up' | 'down'
  caption?: ReactNode
  trend?: number[]
  tone?: StatTone
  /** What the change is against. */
  changeLabel?: string
}) {
  const t = tones[tone]
  const good = change === undefined || change === null || change === 0 ? null : (change > 0) === (goodWhen === 'up')

  return (
    <section aria-label={label} className={`flex items-center gap-3.5 rounded-xl border p-4 shadow-sm 2xl:gap-4 2xl:p-5 ${t.tile}`}>
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full 2xl:h-14 2xl:w-14 ${t.badge}`}>{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-dpl-text/80">{label}</p>
        <p className="truncate text-xl font-bold tabular-nums tracking-tight text-dpl-text 2xl:text-2xl">{value}</p>
        {change !== undefined && (
          <p className={`text-xs font-medium sm:text-sm ${good === null ? 'text-dpl-muted' : good ? 'text-dpl-success' : 'text-dpl-danger'}`}>
            {change === null ? (changeLabel === 'from last month-end' ? 'No earlier month-end' : `Nothing to compare ${changeLabel.replace('from ', 'with ')}`) : `${change > 0 ? '↑' : change < 0 ? '↓' : '→'} ${Math.abs(change).toLocaleString('en-KE')}% ${changeLabel}`}
          </p>
        )}
        {caption && <div className="text-xs text-dpl-muted sm:text-sm">{caption}</div>}
      </div>
      {trend && trend.length > 1 && <Sparkline values={trend} className={`hidden h-10 w-20 shrink-0 sm:block xl:hidden 2xl:block ${t.line}`} />}
    </section>
  )
}

/** A line through the values, scaled to its box; decoration beside the figure it summarises. */
export function Sparkline({ values, className = '' }: { values: number[]; className?: string }) {
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const points = values.map((v, i) => `${(i / (values.length - 1)) * 100},${34 - ((v - min) / span) * 28}`).join(' ')
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className={className} aria-hidden="true">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}
